import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { ToastService, type Toast } from '../../core/services/toast.service';

@Component({
  selector: 'app-toaster',
  imports: [NgClass, MatIcon, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class:
      'pointer-events-none fixed inset-x-4 top-4 z-[100] flex flex-col gap-2 sm:inset-x-auto sm:left-auto sm:right-4 sm:w-full sm:max-w-sm',
    'aria-live': 'polite',
    'aria-atomic': 'true',
  },
  template: `
    @for (t of toasts(); track t.id) {
      <div
        role="status"
        class="pointer-events-auto flex w-full items-start gap-3 rounded-lg border bg-white p-3 text-card-foreground shadow-lg animate-in slide-in-from-right"
        [ngClass]="{
          'border-gray': t.kind === 'info',
          'border-green-500/50': t.kind === 'success',
          'border-yellow-500/60': t.kind === 'warning',
          'border-red-500/60': t.kind === 'error',
        }"
      >
        <span
          class="mt-0.5"
          [ngClass]="{
            'text-gray': t.kind === 'info',
            'text-green-600': t.kind === 'success',
            'text-yellow-600': t.kind === 'warning',
            'text-red-600': t.kind === 'error',
          }"
        >
          <mat-icon class="icon-18" aria-hidden="true">{{ iconFor(t) }}</mat-icon>
        </span>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium leading-tight wrap-break-word">{{ t.title }}</p>
          @if (t.description) {
            <p class="mt-1 text-xs wrap-break-word">
              {{ t.description }}
            </p>
          }
          @if (t.action; as action) {
            <button
              type="button"
              class="mt-2 cursor-pointer text-xs font-medium text-main-purple underline"
              (click)="runAction(t)"
            >
              {{ action.label }}
            </button>
          }
        </div>
        <button
          type="button"
          class="shrink-0 rounded p-1.5 text-gray "
          [attr.aria-label]="'common.dismiss' | transloco"
          (click)="dismiss(t.id)"
        >
          <mat-icon class="icon-14" aria-hidden="true">close</mat-icon>
        </button>
      </div>
    }
  `,
})
export class ToasterComponent {
  private readonly toast = inject(ToastService);

  protected readonly toasts = this.toast.toasts;

  protected iconFor(t: Toast): 'info' | 'check' | 'warning' | 'error' {
    switch (t.kind) {
      case 'success':
        return 'check';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  }

  protected dismiss(id: number): void {
    this.toast.dismiss(id);
  }

  protected runAction(t: Toast): void {
    t.action?.run();
    this.toast.dismiss(t.id);
  }
}
